import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useState } from 'react'
import { useIntl } from 'react-intl'

import { PagePreview } from 'shared/components/PageList/hooks/usePagePreviews'
import {
  DndPageContentData,
  PageTypes
} from 'shared/components/PageList/type'
import {
  CoverTypes,
  LiveUser,
  PageWorkflowStatus
} from 'shared/types/global'

import { Container, Info, Preview } from './Styles'
import ContentPageInfo from './components/ContentPageInfo'
import ContentPagePreview from './components/ContentPagePreview'
import CoverPageInfo from './components/CoverPageInfo'
import CoverPagePreview from './components/CoverPagePreview'
import { PagePositions } from './types'

type PageProps = {
  id: string
  type: PageTypes
  position: PagePositions
  contentData?: DndPageContentData
  isDropProhibited: boolean
  preview: PagePreview
  editingLiveUser?: LiveUser
  onEdit: () => void
  onCopy: () => void
  onView: () => void
  onDelete: () => void
  onStatusChange: (status: PageWorkflowStatus) => void
}

const Page: React.FC<PageProps> = ({
  position,
  id,
  type,
  contentData,
  isDropProhibited,
  preview,
  editingLiveUser,
  onEdit,
  onCopy,
  onView,
  onDelete,
  onStatusChange
}) => {
  const { formatMessage } = useIntl()

  const [isStatusOpen, setIsStatusOpen] = useState(false)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id, disabled: type !== PageTypes.content })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Raise the page above its neighbors so the status dropdown isn't
    // clipped by the adjacent page in the pair.
    zIndex: isDragging ? 9999 : isStatusOpen ? 100 : 1,
    cursor: isDragging
      ? isDropProhibited
        ? 'not-allowed'
        : 'grabbing'
      : 'pointer'
  }

  return (
    <Container
      position={position}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <Preview position={position}>
        {type === PageTypes.coverFront && (
          <CoverPagePreview coverType={CoverTypes.hard} />
        )}
        {type === PageTypes.coverBack && (
          <CoverPagePreview coverType={CoverTypes.soft} />
        )}
        {type === PageTypes.content && (
          <ContentPagePreview
            isDragging={isDragging}
            disableHover={isStatusOpen}
            preview={preview}
            editingLiveUser={editingLiveUser}
            onEdit={onEdit}
            onCopy={onCopy}
            onView={onView}
            onDelete={onDelete}
          />
        )}
      </Preview>

      <Info>
        {type === PageTypes.coverFront && (
          <CoverPageInfo type={type} />
        )}
        {type === PageTypes.coverBack && (
          <CoverPageInfo type={type} />
        )}
        {type === PageTypes.content && (
          <ContentPageInfo
            number={contentData!.number}
            title={formatMessage({
              id: `Page.type.${contentData!.templateType}`
            })}
            status={
              contentData!.workflowStatus ||
              PageWorkflowStatus.default
            }
            onStatusChange={onStatusChange}
            onStatusOpenChange={setIsStatusOpen}
          />
        )}
      </Info>
    </Container>
  )
}

export default Page
